import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Bell, Download } from "lucide-react";

const PAGE_LABEL = {
  "occasions": "Gifts for Occasions",
  "table-grazing": "Table Grazing",
};

export default function AdminNotifySignups() {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi().get("/admin/notify-signups");
      setSignups(r.data || []);
    } catch { setSignups([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm("Delete this signup?")) return;
    try {
      await adminApi().delete(`/admin/notify-signups/${id}`);
      setSignups((prev) => prev.filter((s) => s.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const exportCsv = () => {
    if (!signups.length) return;
    const rows = [["Email", "Page", "Signed up at"]];
    signups.forEach((s) => rows.push([s.email, PAGE_LABEL[s.page] || s.page, s.created_at]));
    const csv = rows.map((r) => r.map((v) => `"${(v || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notify-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const grouped = signups.reduce((acc, s) => {
    const k = s.page || "other";
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="mt-6" data-testid="admin-notify-signups-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-[#5C5555] flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#7A1F2B]"/>
          Emails captured from the "Notify me when live" forms on Coming Soon pages.
          <span className="font-semibold text-[#2D2424]">({signups.length} total)</span>
        </p>
        <button
          type="button"
          onClick={exportCsv}
          disabled={signups.length === 0}
          data-testid="notify-export-csv"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#7A1F2B] text-white text-xs uppercase tracking-wider rounded hover:bg-[#5C1520] disabled:opacity-50"
        >
          <Download className="w-3.5 h-3.5"/> Export CSV
        </button>
      </div>

      {loading ? (
        <p className="text-[#5C5555]">Loading…</p>
      ) : signups.length === 0 ? (
        <div className="text-center py-12 bg-[#FFF6F0] border-2 border-dashed border-[#7A1F2B]/30 rounded-lg">
          <Bell className="w-8 h-8 text-[#7A1F2B]/50 mx-auto"/>
          <p className="mt-3 font-hand text-[#5C5555]">No signups yet — as soon as someone drops their email, you&apos;ll see it here.</p>
        </div>
      ) : (
        Object.keys(grouped).map((page) => (
          <div key={page} className="mb-6" data-testid={`notify-group-${page}`}>
            <h3 className="font-serif text-lg text-[#7A1F2B] uppercase tracking-wider mb-2">
              {PAGE_LABEL[page] || page} <span className="text-xs text-[#5C5555] normal-case font-sans">({grouped[page].length})</span>
            </h3>
            <div className="space-y-2">
              {grouped[page].map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 bg-white border border-[#E5DBD8] px-4 py-2.5 rounded" data-testid={`notify-row-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#2D2424] truncate">{s.email}</p>
                    <p className="text-[10px] text-[#5C5555] tracking-wider uppercase">{new Date(s.created_at).toLocaleString()}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(s.id)}
                    data-testid={`notify-delete-${s.id}`}
                    className="shrink-0 p-1.5 rounded hover:bg-[#FFE7F1] text-[#7A1F2B]"
                    aria-label="delete"
                  >
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
